import {get, post, patch, put, del} from './http';
import api from './client';

/** How far back a text search looks for uploads. */
export type JobRecency = 'week' | 'month' | 'year' | 'forever';

export type Job = {
  id: string;
  search_term: string;
  intent: string | null;
  /** Reference-data language name this investigation is filed under. Display/filter label only. */
  language: string | null;
  target_policy_violations: string[];
  affiliate: string;
  platform: string;
  strategy: string;
  recency: JobRecency;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'timed_out';
  item_count: number;
  scrapy_job_id: string | null;
  folder_id: string | null;
  /** Cron the Scheduler re-runs this job on, in place; null when it has no schedule. */
  schedule_cron: string | null;
  /** False while the schedule is paused. */
  schedule_active: boolean;
  schedule_next_run_at: string | null;
  created_by: string;
  created_at: string;
  completed_at: string | null;
  /** A followers crawl that also collects the profile's pins. */
  collect_pins: boolean;
  /**
   A google_search job's live search progress (null otherwise, and before its search starts):
   pages fetched of the total, and YouTube videos found so far - then how many were sent to collect.
   */
  search_pages_done?: number | null | undefined;
  search_pages_total?: number | null | undefined;
  search_videos_found?: number | null | undefined;
  /** A google_search job's ISO 3166-1 alpha-2 search country (Google's gl); null searches from anywhere. */
  search_country?: string | null | undefined;
  /** A google_search job's search engines; null is Google alone. */
  search_engines?: string[] | null | undefined;
};

export type SetJobScheduleRequest = {
  cron_expression: string;
  is_active: boolean;
  target_policy_violations: string[];
};

export type CreateJobRequest = {
  search_term: string;
  intent?: string;
  language?: string;
  target_policy_violations: string[];
  platform?: string;
  /** 'video' (default, omit), 'channel', 'profile' or 'pin' - which kind of result the query should return. */
  search_type?: 'channel' | 'profile' | 'pin';
  /** Upload window; the API defaults to 'year' when omitted. */
  recency?: JobRecency;
};

export type CreateSingleVideoJobRequest = {
  url_or_video_id: string;
  intent?: string;
  language?: string;
  platform?: string;
};

export type CreatePinterestProfileJobRequest = {
  profile_url_or_username: string;
  intent?: string;
  language?: string;
  /** 0 the profile only, 1 its followers, 2 their followers too (Falcon only). */
  follower_depth?: 0 | 1 | 2;
  collect_pins?: boolean;
  collect_boards?: boolean;
  target_policy_violations: string[];
};

export type CreatePinterestBoardJobRequest = {
  board_url_or_path: string;
  intent?: string;
  language?: string;
  target_policy_violations: string[];
};

export type CreateFollowerSweepJobRequest = {
  source_classification_id: string;
  bypass_follower_limit?: boolean;
};

/** Multipart, not JSON - `jobsApi.createImage` turns this into the FormData the endpoint binds. */
export type CreateImageJobRequest = {
  file: File;
  intent?: string;
  language?: string;
  targetPolicyViolations: string[];
};

export type CreateGoogleSearchJobRequest = {
  search_term: string;
  intent?: string;
  language?: string;
  recency?: JobRecency;
  /** ISO 3166-1 alpha-2 code to search Google from. */
  country?: string;
  /** Search engines to run the query on; omitted is Google alone. */
  engines?: string[];
  target_policy_violations: string[];
};

export type CreatePlaylistJobRequest = {
  playlist_url_or_id: string;
  intent?: string;
  language?: string;
  target_policy_violations: string[];
};

export type CreateChannelJobRequest = {
  channel_url_or_handle_or_id: string;
  intent?: string;
  language?: string;
  target_policy_violations: string[];
};

/** Pulls the playlist id out of a pasted playlist/watch URL, else assumes it's already a bare id. */
function extractPlaylistId(raw: string): string {
  const match = /[&?]list=([\w-]+)/.exec(raw);
  return match?.[1] ?? raw;
}

/** Pulls the channel id/handle out of a pasted channel URL, else assumes it's already bare. */
function extractChannelIdentifier(raw: string): string {
  const channelIdMatch = /\/channel\/([\w-]+)/.exec(raw);
  if (channelIdMatch?.[1]) {
    return channelIdMatch[1];
  }

  const handleMatch = /\/@([^/?]+)/.exec(raw);
  return handleMatch?.[1] ? `@${handleMatch[1]}` : raw;
}

// Job.search_term stores whatever was typed/pasted verbatim (a full URL, in most cases) so the
// backend can classify it at dispatch time - this is purely a display-layer cleanup, not what
// gets sent to Scrapy.
export function jobDisplayLabel(job: Pick<Job, 'search_term' | 'strategy'>) {
  switch (job.strategy) {
    case 'image_search': {
      return 'Image search';
    }

    case 'youtube_playlist': {
      return extractPlaylistId(job.search_term);
    }

    case 'youtube_channel': {
      return extractChannelIdentifier(job.search_term);
    }

    case 'pinterest_board': {
      // SearchTerm is the canonical "{handle}/{board}" path (see JobStrategies.PinterestBoard) -
      // show just the board slug, the part an analyst actually recognizes.
      return job.search_term.split('/').pop() ?? job.search_term;
    }

    default: {
      return job.search_term;
    }
  }
}

export type ImageMatchTier = 'Exact' | 'PageConfirmed' | 'Partial' | 'PageOnly';

export type ImageSearchMatches = {
  pages_matched: number;
  last_scanned_at: string;
  tier_counts: Partial<Record<ImageMatchTier, number>>;
  matches: Array<{video_id: string; tier: ImageMatchTier}>;
  // Pages Vision found displaying the image that aren't this platform's own content - a repost,
  // storefront, or other off-platform presence. See ImageSearchOffPlatformPage.
  off_platform_page_urls: string[];
};

// What each tier means to an analyst reading a result row. Exact and PageConfirmed both mean "this
// item's own thumbnail/image matched"; Partial means a crop or derivative; PageOnly means the image
// was found on a page about this item but wasn't confirmed as this item's own image - most often a
// recommended item's thumbnail elsewhere on the page, which is the weakest evidence Vision returns.
export const imageMatchTierLabels: Record<ImageMatchTier, string> = {
  Exact: 'Exact',
  PageConfirmed: 'On page',
  Partial: 'Crop',
  PageOnly: 'Page only'
};

export type JobList = {
  results: Job[];
  page: number;
  page_size: number;
  has_more: boolean;
  total: number;
};

// Sidebar page size for the investigations job list - kept moderate since every "Load more"
// scroll trigger re-fetches this many rows.
export const JOBS_PAGE_SIZE = 30;

/**
 Server-side filters for `GET /api/jobs`. `folder_id` and `unfiled` pick one sidebar section
 and are mutually exclusive (the API answers 400 otherwise); the rest narrow it the way the
 sidebar's search box and chips do.
 */
export type JobListQuery = {
  affiliate?: string;
  page?: number;
  page_size?: number;
  folder_id?: string;
  include_subfolders?: boolean;
  unfiled?: boolean;
  q?: string;
  strategy?: string[];
  policy_violation?: string[];
  language?: string[];
  mine?: boolean;
  created_by?: string[];
  /** Only jobs with a schedule, active or paused. */
  scheduled?: boolean;
  /** Just these jobs (at most 100), for the status poll. */
  ids?: string[];
};

/** Every language and policy violation in use across the investigations the caller can see. */
export type JobFacets = {
  languages: string[];
  policy_violations: string[];
  /** Lower-cased emails of everyone who created a folder or investigation the caller can see. */
  creators: string[];
};

export const jobsApi = {
  list: async (parameters?: JobListQuery) => get<JobList>('/jobs', parameters),
  facets: async () => get<JobFacets>('/jobs/facets'),

  get: async (id: string) => get<Job>(`/jobs/${id}`),

  create: async (body: CreateJobRequest) => post<CreateJobRequest, Job>('/jobs', body),

  createSingle: async (body: CreateSingleVideoJobRequest) =>
    post<CreateSingleVideoJobRequest, Job>('/jobs/single', body),

  createPinterestProfile: async (body: CreatePinterestProfileJobRequest) =>
    post<CreatePinterestProfileJobRequest, Job>('/jobs/pinterest-profile', body),

  createPinterestBoard: async (body: CreatePinterestBoardJobRequest) =>
    post<CreatePinterestBoardJobRequest, Job>('/jobs/pinterest-board', body),

  createFollowerSweep: async (body: CreateFollowerSweepJobRequest) =>
    post<CreateFollowerSweepJobRequest, Job>('/jobs/pinterest-follower-sweep', body),

  async createImage({file, intent, language, targetPolicyViolations}: CreateImageJobRequest) {
    const formData = new FormData();
    formData.append('image', file);
    if (intent) {
      formData.append('intent', intent);
    }

    if (language) {
      formData.append('language', language);
    }

    for (const policyViolation of targetPolicyViolations) {
      formData.append('targetPolicyViolations', policyViolation);
    }

    return post<FormData, Job>('/jobs/image', formData);
  },

  createGoogleSearch: async (body: CreateGoogleSearchJobRequest) =>
    post<CreateGoogleSearchJobRequest, Job>('/jobs/google', body),
  createPlaylist: async (body: CreatePlaylistJobRequest) =>
    post<CreatePlaylistJobRequest, Job>('/jobs/playlist', body),

  createChannel: async (body: CreateChannelJobRequest) =>
    post<CreateChannelJobRequest, Job>('/jobs/channel', body),

  /**
   The image an image_search job was searched with - a plain get<> can't request a blob, so this
   bypasses it and uses the shared axios instance (still auth-header'd) directly.
   */
  async getImage(id: string) {
    const {data} = await api.get<Blob>(`/jobs/${id}/image`, {responseType: 'blob'});
    return data;
  },

  /**
   The evidence behind an image_search job's results: how much Cloud Vision found in total, and the
   tier of every video it matched. Separate from the job payload because the job list would
   otherwise pay for this on every row.
   */
  getImageMatches: async (id: string) => get<ImageSearchMatches>(`/jobs/${id}/image-matches`),

  setFolder: async (id: string, folderId: string | null) =>
    patch<{folder_id: string | null}, Job>(`/jobs/${id}/folder`, {folder_id: folderId}),

  rerun: async (id: string) => post<Record<string, never>, Job>(`/jobs/${id}/rerun`, {}),

  setSchedule: async (id: string, body: SetJobScheduleRequest) =>
    put<SetJobScheduleRequest, Job>(`/jobs/${id}/schedule`, body),

  clearSchedule: async (id: string) => del<Job>(`/jobs/${id}/schedule`),

  remove: async (id: string) => del(`/jobs/${id}`)
};
